import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmDialog } from './ConfirmDialog'
import { useFormikContext } from 'formik'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import _ from 'lodash'
import { formatFieldKey } from '../../utils/formatFieldKey'

const RouteLeavingGuard = <T extends object>(): JSX.Element => {
  const t = useAppTranslations()
  const navigate = useNavigate()
  const [modalVisible, setModalVisible] = useState(false)
  const [nextPath, setNextPath] = useState<string | null>(null)
  const { dirty, values, initialValues } = useFormikContext<T>()
  const [changedFields, setChangedFields] = useState<string[]>([])

  const findChangedFields = (): void => {
    const initial = initialValues as Record<string, any>
    const current = values as Record<string, any>

    const changedFields: string[] = []

    const deepCompare = (initialObj: any, currentObj: any, parentKey = ''): void => {
      Object.keys(initialObj).forEach((key) => {
        const fullKey = parentKey ? `${parentKey}.${key}` : key

        if (_.isArray(initialObj[key]) && initialObj[key].every(_.isString)) {
          if (!_.isEqual(initialObj[key], currentObj[key])) changedFields.push(parentKey || key)
          return
        }

        if (_.isObject(initialObj[key]) && _.isObject(currentObj[key]))
          deepCompare(initialObj[key], currentObj[key], fullKey)
        else if (!_.isEqual(initialObj[key], currentObj[key])) changedFields.push(fullKey)
      })
    }
    deepCompare(initial, current)
    setChangedFields(_.uniq(changedFields))
  }

  useEffect(() => {
    if (dirty) findChangedFields()
  }, [dirty, initialValues, values])

  const closeModal = (): void => {
    setModalVisible(false)
    setNextPath(null)
  }

  const handleConfirmNavigationClick = (): void => {
    setModalVisible(false)
    if (nextPath) navigate(nextPath)
  }

  useEffect(() => {
    if (!(dirty && changedFields.length > 0)) return undefined

    const handleClick = (event: MouseEvent): void => {
      const target = event.target as Element | null
      const link = target?.closest('a')
      if (!link) return
      if (event.defaultPrevented) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      if (link.target === '_blank') return

      const href = link.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return

      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin) return

      const destination = `${url.pathname}${url.search}${url.hash}`
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`
      if (destination === current) return

      event.preventDefault()
      setModalVisible(true)
      setNextPath(destination)
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }

    document.addEventListener('click', handleClick, true)
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      document.removeEventListener('click', handleClick, true)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [dirty, changedFields.length])

  const listOfChangedFields =
    dirty && changedFields.length > 0 ? (
      <ul>
        {changedFields.map(formatFieldKey).map((field, index) => (
          <li key={index}>{field}</li>
        ))}
      </ul>
    ) : null

  return (
    <>
      <ConfirmDialog
        message={
          <>
            <p>{t('commons.unsavedChangesWarning')}</p>
            <p>{t('commons.unsavedModifications')}</p>
            {listOfChangedFields}
          </>
        }
        actionButton={{
          label: t('commons.actions.quitWithoutSaving'),
          action: handleConfirmNavigationClick,
        }}
        setOpen={closeModal}
        open={modalVisible}
      />
    </>
  )
}
export default RouteLeavingGuard
